import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Plus, 
  Search, 
  MoreVertical, 
  Edit,
  Trash2,
  Calendar,
  DollarSign,
  ToggleLeft,
  ToggleRight,
  Briefcase
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatCurrency, Currency } from "@/lib/currency";
import { ProfessionalTerminology } from "@/components/ProfessionalSidebar";

interface Service {
  id: string;
  name: string;
  description: string | null;
  frequency: string;
  amount_type: string;
  fixed_amount: number | null;
  currency: string;
  tranches_enabled: boolean;
  is_active: boolean;
  created_at: string;
}

interface ServicesListProps {
  merchantId: string;
  currency: string;
  terminology?: ProfessionalTerminology;
  onServiceSelect?: (service: Service) => void;
}

const FREQUENCY_OPTIONS = [
  { value: "one_time", label: "One Time" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annual", label: "Annual" },
  { value: "staggered", label: "Staggered/Custom" },
];

const AMOUNT_TYPE_OPTIONS = [
  { value: "fixed", label: "Fixed Amount" },
  { value: "custom", label: "Custom per Client" },
  { value: "client_enters", label: "Client Enters Amount" },
];

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function ServicesList({ merchantId, currency, terminology = {}, onServiceSelect }: ServicesListProps) {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    frequency: "monthly",
    amount_type: "fixed",
    fixed_amount: "",
    tranches_enabled: false,
  });

  const serviceLabel = terminology.products || "Service";
  const servicesLabel = terminology.products ? `${terminology.products}` : "Services";

  useEffect(() => {
    fetchServices();
  }, [merchantId]);

  const fetchServices = async () => {
    try {
      const { data, error } = await supabase
        .from("professional_services")
        .select("*")
        .eq("merchant_id", merchantId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setServices(data || []);
    } catch (error) {
      console.error("Error fetching services:", error);
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = async () => {
    if (!formData.name.trim()) {
      toast.error("Service name is required");
      return;
    }

    if (formData.amount_type === "fixed" && !formData.fixed_amount) {
      toast.error("Fixed amount is required");
      return;
    }

    try {
      const { error } = await supabase.from("professional_services").insert({
        merchant_id: merchantId,
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        frequency: formData.frequency,
        amount_type: formData.amount_type,
        fixed_amount: formData.amount_type === "fixed" ? parseFloat(formData.fixed_amount) : null,
        currency,
        tranches_enabled: formData.tranches_enabled,
      });

      if (error) throw error;

      toast.success(`${serviceLabel} created successfully`);
      setIsAddDialogOpen(false);
      resetForm();
      fetchServices();
    } catch (error) {
      console.error("Error adding service:", error);
      toast.error("Failed to create service");
    }
  };

  const handleEditService = async () => {
    if (!selectedService || !formData.name.trim()) {
      toast.error("Service name is required");
      return;
    }

    try {
      const { error } = await supabase
        .from("professional_services")
        .update({
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          frequency: formData.frequency,
          amount_type: formData.amount_type,
          fixed_amount: formData.amount_type === "fixed" ? parseFloat(formData.fixed_amount) : null,
          tranches_enabled: formData.tranches_enabled,
        })
        .eq("id", selectedService.id);

      if (error) throw error;

      toast.success(`${serviceLabel} updated successfully`);
      setIsEditDialogOpen(false);
      setSelectedService(null);
      resetForm();
      fetchServices();
    } catch (error) {
      console.error("Error updating service:", error);
      toast.error("Failed to update service");
    }
  };

  const handleToggleActive = async (service: Service) => {
    try {
      const { error } = await supabase
        .from("professional_services")
        .update({ is_active: !service.is_active })
        .eq("id", service.id);

      if (error) throw error;

      toast.success(`${serviceLabel} ${service.is_active ? "deactivated" : "activated"}`);
      fetchServices();
    } catch (error) {
      console.error("Error toggling service:", error);
      toast.error("Failed to update service");
    }
  };

  const handleDeleteService = async (service: Service) => {
    if (!confirm(`Are you sure you want to delete "${service.name}"?`)) return;

    try {
      const { error } = await supabase
        .from("professional_services")
        .delete()
        .eq("id", service.id);

      if (error) throw error;

      toast.success(`${serviceLabel} deleted`);
      fetchServices();
    } catch (error) {
      console.error("Error deleting service:", error);
      toast.error("Failed to delete service");
    }
  };

  const openEditDialog = (service: Service) => {
    setSelectedService(service);
    setFormData({
      name: service.name,
      description: service.description || "",
      frequency: service.frequency,
      amount_type: service.amount_type,
      fixed_amount: service.fixed_amount?.toString() || "",
      tranches_enabled: service.tranches_enabled || false,
    });
    setIsEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      frequency: "monthly",
      amount_type: "fixed",
      fixed_amount: "",
      tranches_enabled: false,
    });
  };

  const filteredServices = services.filter(
    (service) =>
      service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (service.description && service.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const getFrequencyLabel = (freq: string) => {
    return FREQUENCY_OPTIONS.find((f) => f.value === freq)?.label || freq;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-cyan-500 border-t-transparent rounded-full mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading {servicesLabel.toLowerCase()}...</p>
        </CardContent>
      </Card>
    );
  }

  const serviceFormFields = (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Name *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Enter service name"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Describe this service..."
          rows={2}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Billing Frequency</Label>
          <Select
            value={formData.frequency}
            onValueChange={(value) => setFormData(prev => ({ ...prev, frequency: value }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FREQUENCY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Amount Type</Label>
          <Select
            value={formData.amount_type}
            onValueChange={(value) => setFormData(prev => ({ ...prev, amount_type: value }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AMOUNT_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {formData.amount_type === "fixed" && (
        <div className="space-y-2">
          <Label htmlFor="amount">Amount ({currency})</Label>
          <Input
            id="amount"
            type="number"
            min="0"
            step="0.01"
            value={formData.fixed_amount}
            onChange={(e) => setFormData(prev => ({ ...prev, fixed_amount: e.target.value }))}
            placeholder="Enter amount"
          />
        </div>
      )}
      {formData.frequency === "staggered" && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
          <div>
            <p className="font-medium text-sm">Enable Tranches</p>
            <p className="text-xs text-muted-foreground">Split payment into multiple installments</p>
          </div>
          <Switch
            checked={formData.tranches_enabled}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, tranches_enabled: checked }))}
          />
        </div>
      )}
    </div>
  );

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle>{servicesLabel}</CardTitle>
              <CardDescription>
                Create reusable {servicesLabel.toLowerCase()} templates for quick billing
              </CardDescription>
            </div>
            <Button
              onClick={() => {
                resetForm();
                setIsAddDialogOpen(true);
              }}
              className="bg-cyan-600 hover:bg-cyan-700 gap-2"
            >
              <Plus className="h-4 w-4" />
              Add {serviceLabel}
            </Button>
          </div>
        </CardHeader>
        <CardContent>

          {/* Services List */}
          {filteredServices.length === 0 ? (
            <div className="text-center py-10">
              <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-4">
                {searchQuery ? `No ${servicesLabel.toLowerCase()} found` : `No ${servicesLabel.toLowerCase()} yet`}
              </p>
              {!searchQuery && (
                <p className="text-sm">Use the <strong>Add {serviceLabel}</strong> button above to get started</p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredServices.map((service) => (
                <div
                  key={service.id}
                  className={`flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors ${
                    !service.is_active ? "opacity-60" : ""
                  }`}
                >
                  <div 
                    className="flex items-center gap-4 flex-1 min-w-0 cursor-pointer"
                    onClick={() => onServiceSelect?.(service)}
                  >
                    <div className="h-10 w-10 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center flex-shrink-0">
                      <Briefcase className="h-5 w-5 text-cyan-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium truncate">{service.name}</p>
                        {!service.is_active && (
                          <Badge variant="secondary" className="text-xs">Inactive</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {getFrequencyLabel(service.frequency)}
                        </span>
                        {service.amount_type === "fixed" && service.fixed_amount && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-3 w-3" />
                            {formatCurrency(service.fixed_amount, service.currency as Currency)}
                          </span>
                        )}
                        {service.amount_type !== "fixed" && (
                          <span className="text-xs">
                            {AMOUNT_TYPE_OPTIONS.find((a) => a.value === service.amount_type)?.label}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(service)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleActive(service)}>
                          {service.is_active ? (
                            <>
                              <ToggleLeft className="h-4 w-4 mr-2" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <ToggleRight className="h-4 w-4 mr-2" />
                              Activate
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDeleteService(service)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Service Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New {serviceLabel}</DialogTitle>
            <DialogDescription>
              Set up a reusable {serviceLabel.toLowerCase()} template
            </DialogDescription>
          </DialogHeader>
          {serviceFormFields}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddService} className="bg-cyan-600 hover:bg-cyan-700">
              Create {serviceLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Service Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {serviceLabel}</DialogTitle>
            <DialogDescription>
              Update the {serviceLabel.toLowerCase()} template
            </DialogDescription>
          </DialogHeader>
          {serviceFormFields}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditService} className="bg-cyan-600 hover:bg-cyan-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
